import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PortfolioAddDialogFields from "../../field/portfolio/PortfolioAddDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import FileInput, {File} from "../../../../shared/components/FileInput";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	FileDataSource,
	Functions,
	Information,
	PortfolioAdd,
	PortfolioFile,
	PortfolioFileInterface,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxPortfolioPortfolio} from "../../../../shared/models/PortalToolboxPortfolioPortfolio";
import {PortalPortfolioAdd} from "../../../../shared/models/PortalPortfolioAdd";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import {InputSwitch} from "primereact/inputswitch";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface PortfolioAddFileDialogStatus extends PortalDialogState<PortalToolboxPortfolioPortfolio> {
	portfolioFile: File;
}

export class PortfolioAddFileDialogDefinition extends PortalDialog<PortalToolboxPortfolioPortfolio,
	PortalDialogProperties<PortalToolboxPortfolioPortfolio>, PortfolioAddFileDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<PortfolioAddDialogFields, string>(),
			portfolioFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.portfolio, Functions.getPortfolioDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.PORTFOLIO_ADD_FILE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkPortfolio: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxPortfolioPortfolio | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalPortfolioAdd = parameter!.add as PortalPortfolioAdd;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-files">
							<Translate {...this.props} data="portfolio.add.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<FileInput inputId="control-files" value={this.state.portfolioFile.value}
									   filename={this.state.portfolioFile.name}
									   onChange={(files) => this.onChange(
										   PortfolioAddDialogFields.FILES, files[0]
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-initial-file">
							<Translate {...this.props} data="portfolio.add.initial" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-initial-file"
										 checked={FunctionHelper.applyValue(addParameter.initialFile, PortalPortfolioAdd.getInitialFileDefault())}
										 onChange={(e) => this.onChange(
											 PortfolioAddDialogFields.INITIAL_FILE, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["portfolio-add-file-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.portfolio.add.file"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PortfolioAddDialogFields, value: any): void {
		let tempParameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data;
		let addParameter: PortalPortfolioAdd = tempParameter.add as PortalPortfolioAdd;

		switch (field) {
			case PortfolioAddDialogFields.FILES:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				let fileDataType: PortfolioFile = PortfolioFile.fromJson({
					fileName: file.name,
					mimeType: file.mimetype,
					path: addParameter.path,
					data: {
						source: FileDataSource.Value,
						value: file.value
					}
				} as PortfolioFileInterface);
				addParameter.file = [fileDataType];

				this.setState({
					portfolioFile: file,
					parameter: this.state.parameter
				});
				return;
			case PortfolioAddDialogFields.INITIAL_FILE:
				addParameter.initialFile = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-add small"}/>
					<Translate {...this.props} data="portfolio.add.title.file" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data.clone();
		let addParameter: PortalPortfolioAdd = parameter.add as PortalPortfolioAdd;

		if (addParameter.initialFile) {
			addParameter.initialFileName = this.state.portfolioFile.name;
			addParameter.initialDocumentPath = addParameter.path;
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			portfolio: parameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}


			let message: Message = new Message("success.toolbox.portfolio.add.file", MessageType.SUCCESS);
			this.props.context.getPage().addMessage(message);
		}

		this.hide();
	}

	protected isValid(): boolean {
		let tempParameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data;
		let addParameter: PortalPortfolioAdd = tempParameter.add as PortalPortfolioAdd;

		if (FunctionHelper.applyValue(addParameter.file, PortfolioAdd.getFileDefault()).length === 0) {
			return false;
		}

		return super.isValid();
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxPortfolioPortfolio | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return;
		}

		let addParameter: PortalPortfolioAdd = parameter!.add as PortalPortfolioAdd;

		// clear old data
		addParameter.file = [];

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			portfolioFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});
	};

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.PORTFOLIO_ADD_FILE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxPortfolioPortfolio> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			portfolioFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});
	}
}

const PortfolioAddFileDialog = React.memo(PortfolioAddFileDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PortfolioAddFileDialog;